// Port GPS : implémentation navigateur aujourd'hui, Capacitor plus tard (suivi en arrière-plan).

export interface GeoFix {
  latitude: number
  longitude: number
  accuracy: number | null
  heading: number | null
  speed: number | null
  /** Heure de la mesure fournie par l'appareil (ms). */
  timestamp: number
}

export type GeoFailure = 'PERMISSION_DENIED' | 'POSITION_UNAVAILABLE' | 'UNSUPPORTED'
export type GeoPermission = 'granted' | 'denied' | 'prompt' | 'unknown'

export interface GeolocationPort {
  supported: boolean
  permission: () => Promise<GeoPermission>
  /** Démarre le suivi; retourne l'arrêt. */
  watch: (onFix: (fix: GeoFix) => void, onFailure: (failure: GeoFailure) => void) => () => void
}

function finite(value: number | null | undefined): number | null {
  return value === null || value === undefined || !Number.isFinite(value) ? null : value
}

export function createBrowserGeolocation(): GeolocationPort {
  const supported = typeof navigator !== 'undefined' && 'geolocation' in navigator
  return {
    supported,
    async permission() {
      if (!supported) return 'denied'
      try {
        const status = await navigator.permissions.query({ name: 'geolocation' })
        return status.state
      } catch {
        return 'unknown'
      }
    },
    watch(onFix, onFailure) {
      if (!supported) {
        onFailure('UNSUPPORTED')
        return () => undefined
      }
      const id = navigator.geolocation.watchPosition(
        (result) => {
          onFix({
            latitude: result.coords.latitude,
            longitude: result.coords.longitude,
            accuracy: finite(result.coords.accuracy),
            heading: finite(result.coords.heading),
            speed: finite(result.coords.speed),
            timestamp: result.timestamp,
          })
        },
        (error) => {
          onFailure(error.code === error.PERMISSION_DENIED ? 'PERMISSION_DENIED' : 'POSITION_UNAVAILABLE')
        },
        // maximumAge 0 : jamais une position en cache présentée comme une nouvelle mesure.
        { enableHighAccuracy: true, maximumAge: 0, timeout: 30_000 },
      )
      return () => {
        navigator.geolocation.clearWatch(id)
      }
    },
  }
}

export interface SimulatedGeolocation extends GeolocationPort {
  /** Envoie une position fictive à tous les abonnés; le GPS du navigateur est ensuite ignoré. */
  emit: (fix: GeoFix) => void
  /** Rend la main au GPS du navigateur. */
  release: () => void
}

/**
 * GPS du navigateur remplaçable par des positions fictives (mode fixtures avec laboratoire seulement : essais et
 * démonstrations). Plusieurs abonnés possibles : télémétrie de 30 s et moteur de détection.
 */
export function createSimulatedGeolocation(base: GeolocationPort): SimulatedGeolocation {
  const listeners = new Set<{ onFix: (fix: GeoFix) => void; onFailure: (failure: GeoFailure) => void }>()
  let simulated = false
  return {
    supported: true,
    permission: () => (simulated ? Promise.resolve('granted') : base.permission()),
    watch(onFix, onFailure) {
      const listener = { onFix, onFailure }
      listeners.add(listener)
      const stopBase = base.watch(
        (fix) => {
          if (!simulated) onFix(fix)
        },
        (failure) => {
          if (!simulated) onFailure(failure)
        },
      )
      return () => {
        listeners.delete(listener)
        stopBase()
      }
    },
    emit(fix) {
      simulated = true
      for (const listener of listeners) listener.onFix(fix)
    },
    release() {
      simulated = false
    },
  }
}
